import { ChangeEventHandler, FocusEventHandler, forwardRef } from "react";
import { ErrorMessage } from "@hookform/error-message";
import { useFormState } from "react-hook-form";
import { useTranslation } from "next-i18next";

import ErrorMessageComponent from "./error-message-component";

import { classNames } from "@utils/class-names";

export interface TextInputProps {
  name: string;
  placeholder: string;
  value?: string;
  setValue?(value: string): void;
  label?: string;
  showLabel?: boolean;
  required?: boolean;
  onChange?: ChangeEventHandler<HTMLInputElement>;
  onBlur?: FocusEventHandler<HTMLInputElement>;
  disabled?: boolean;
  format?(value: string): string;
  autoComplete?: string;
  hideErrors?: boolean;
}

const TextInput = forwardRef<HTMLInputElement, TextInputProps>(
  (
    {
      name,
      placeholder,
      label,
      showLabel,
      required,
      value,
      setValue,
      onChange,
      onBlur,
      disabled,
      format,
      autoComplete,
      hideErrors,
    },
    ref
  ) => {
    const { errors } = useFormState();
    const { t } = useTranslation("forms", { keyPrefix: "validation" });

    const hasErrors = !!name
      .split(".")
      .reduce((acc, curr) => acc && acc[curr], errors);

    const nameLowercase = name.toLowerCase();

    return (
      <label htmlFor={name} className="block">
        <span className={classNames(showLabel ? "mb-3" : "sr-only")}>
          {showLabel ? label : placeholder}
          {required ? "*" : ""}
        </span>
        <input
          {...{
            id: name,
            name,
            placeholder,
            value: !ref ? value : undefined,
            onChange: (event) => {
              if (format) {
                event.target.value = format(event.target.value);
              }
              if (onChange) {
                onChange(event);
              }
              if (setValue) {
                setValue(event.target.value);
              }
            },
            onBlur,
            ref,
            type: nameLowercase.includes("email")
              ? "email"
              : nameLowercase.includes("password")
              ? "password"
              : "text",
            disabled,
            autoComplete,
          }}
          className={classNames(
            "block w-full h-10 outline-none focus:outline-none rounded border border-black focus:border-black focus:ring-4 focus:ring-gray-200 focus:ring-opacity-80 focus:placeholder-gray-400",
            required ? "border-l-2" : "",
            disabled ? "bg-gray-400" : "",
            hasErrors ? "!ring-red-200" : ""
          )}
        />
        {!hideErrors && (
          <ErrorMessage
            name={name}
            errors={errors}
            as="div"
            render={({ message }) => (
              <ErrorMessageComponent
                {...{
                  errors: message.startsWith("min")
                    ? t("min", { min: message.slice(3) })
                    : message.startsWith("max")
                    ? t("max", { max: message.slice(3) })
                    : t(message),
                }}
              />
            )}
          />
        )}
      </label>
    );
  }
);

TextInput.displayName = "TextInput";

export default TextInput;
